import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { DictionaryControllerService, SportsClubControllerService, TeamControllerService } from '../api/index.ts';
import { selectUserId } from '../store/userSlice';
import { toast } from 'react-toastify';
import './SearchClubs.css';

function SearchClubs() {
  const userId = useSelector(selectUserId);

  const [dictionaries, setDictionaries] = useState({
    territories: [],
    seniorTiers: [],
    youthTiers: []
  });

  const [searchMode, setSearchMode] = useState('FILTER'); // 'FILTER' or 'DISTANCE'
  const [maxDistanceKm, setMaxDistanceKm] = useState(50);

  const [searchCriteria, setSearchCriteria] = useState({
    county: '',
    voivodeship: '',
    seniorTier: ''
  });

  const [results, setResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDictionaries, setIsLoadingDictionaries] = useState(true);
  const [error, setError] = useState('');

  const [showTeamsModal, setShowTeamsModal] = useState(false);
  const [selectedClubTeams, setSelectedClubTeams] = useState([]);
  const [selectedClubName, setSelectedClubName] = useState('');
  const [isLoadingTeams, setIsLoadingTeams] = useState(false);
  const [sendingQueryFor, setSendingQueryFor] = useState(null);

  const [showMessageModal, setShowMessageModal] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [pendingMatch, setPendingMatch] = useState(null);

  // Pobierz słowniki przy montowaniu
  useEffect(() => {
    const fetchDictionaries = async () => {
      try {
        const data = await DictionaryControllerService.getSeniorTeamsSearchDictionaries();

        setDictionaries({
          territories: data.territories || [],
          seniorTiers: data.tiers || [],
          youthTiers: []
        });
      } catch (error) {
        setError('Nie udało się pobrać słowników');
      } finally {
        setIsLoadingDictionaries(false);
      }
    };

    fetchDictionaries();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Jeśli zmienia się województwo, resetuj powiat
    if (name === 'voivodeship') {
      setSearchCriteria(prev => ({
        ...prev,
        voivodeship: value,
        county: ''
      }));
    } else {
      setSearchCriteria(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Helper do pobierania powiatów dla wybranego województwa
  const getCountiesForVoivodeship = () => {
    if (!searchCriteria.voivodeship) {
      return [];
    }
    const territory = dictionaries.territories.find(t => t.key === searchCriteria.voivodeship);
    return territory?.subValues || [];
  };

  const performSearch = async () => {
    setIsLoading(true);
    setError('');

    try {
      const criteria = searchMode === 'DISTANCE'
        ? {
            tier: searchCriteria.seniorTier || undefined,
            searchMode: 'DISTANCE',
            maxDistanceKm: maxDistanceKm || 50
          }
        : {
            county: searchCriteria.county || undefined,
            voivodeship: searchCriteria.voivodeship || undefined,
            tier: searchCriteria.seniorTier || undefined
          };

      const response = await TeamControllerService.searchTeams(criteria);

      setResults(response.content || []);

      if (!response.content || response.content.length === 0) {
        setError(searchMode === 'DISTANCE'
          ? 'Nie znaleziono drużyn w podanej odległości. Spróbuj zwiększyć zasięg lub upewnij się, że Twój klub ma ustawione miasto.'
          : 'Nie znaleziono drużyn spełniających kryteria');
      }
    } catch (error) {
      setError('Wystąpił błąd podczas wyszukiwania');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await performSearch();
  };

  const handleReset = () => {
    setSearchCriteria({
      county: '',
      voivodeship: '',
      seniorTier: ''
    });
    setMaxDistanceKm(50);
    setResults([]);
    setError('');
  };

  const handleSendQuery = (sportsClubId, sportsClubName, teamId) => {
    if (!userId) {
      toast.error('Musisz być zalogowany, aby wysłać zapytanie');
      return;
    }

    // Zapisz dane zapytania i otwórz okno dialogowe
    setPendingMatch({
      sportsClubId,
      sportsClubName,
      teamId
    });
    setMessageText('');
    setShowMessageModal(true);
  };

  const handleConfirmSendQuery = async () => {
    if (!pendingMatch) return;

    const { sportsClubId, sportsClubName, teamId } = pendingMatch;

    setSendingQueryFor(teamId);
    setShowMessageModal(false);

    toast.error('Funkcja wysyłania zapytań jest obecnie niedostępna');
    setSendingQueryFor(null);
    setPendingMatch(null);
  };

  const handleCancelSendQuery = () => {
    setShowMessageModal(false);
    setPendingMatch(null);
    setMessageText('');
  };

  const handleShowAllTeams = async (sportsClubId, sportsClubName) => {
    setIsLoadingTeams(true);
    setSelectedClubName(sportsClubName);
    setShowTeamsModal(true);

    try {
      const teams = await TeamControllerService.getTeamsBySportsClubId(sportsClubId);
      setSelectedClubTeams(teams || []);
    } catch (error) {
      // error handled silently
      setSelectedClubTeams([]);
    } finally {
      setIsLoadingTeams(false);
    }
  };

  // Funkcje formatujące dla wyświetlania drużyn
  const formatSeniorTier = (tier) => {
    const labels = {
      'EKSTRAKLASA': 'Ekstraklasa',
      'FIRST_LEAGUE': 'I Liga',
      'SECOND_LEAGUE': 'II Liga',
      'THIRD_LEAGUE': 'III Liga',
      'FOURTH_LEAGUE': 'IV Liga',
      'FIFTH_LEAGUE': 'V Liga',
      'REGIONAL_CLASS': 'Klasa Okręgowa',
      'CLASS_A': 'Klasa A',
      'CLASS_B': 'Klasa B',
      'CLASS_C': 'Klasa C'
    };
    return labels[tier] || tier;
  };

  const formatSeniorTeamType = (type) => {
    const labels = {
      'FIRST_TEAM': 'Pierwsza drużyna',
      'SECOND_TEAM': 'Druga drużyna',
      'THIRD_TEAM': 'Trzecia drużyna'
    };
    return labels[type] || type;
  };

  const formatYouthTier = (tier) => {
    const labels = {
      'CLJ': 'CLJ',
      'VOIVODESHIP_I': 'Wojewódzka I',
      'VOIVODESHIP_2': 'Wojewódzka II',
      'VOIVODESHIP_3': 'Wojewódzka III',
      'REGIONAL': 'Regionalna'
    };
    return labels[tier] || tier;
  };

  const formatAgeCategory = (category) => {
    const labels = {
      'JUNIOR_STARSZY': 'Junior starszy',
      'JUNIOR_MLODSZY': 'Junior młodszy',
      'TRAMPKARZ': 'Trampkarz',
      'MLODZIK': 'Młodzik',
      'ORLIK': 'Orlik',
      'ZAK': 'Żak',
      'SKRZAT': 'Skrzat'
    };
    return labels[category] || category;
  };

  if (isLoadingDictionaries) {
    return (
      <div className="search-container">
        <div className="loading">Ładowanie...</div>
      </div>
    );
  }

  return (
    <div className="search-container">
      <form onSubmit={handleSearch} className="search-form">
        <div className="search-mode-toggle">
          <label className="radio-label">
            <input
              type="radio"
              name="searchMode"
              value="FILTER"
              checked={searchMode === 'FILTER'}
              onChange={() => { setSearchMode('FILTER'); setResults([]); setError(''); }}
            />
            <span>Szukaj po filtrach</span>
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name="searchMode"
              value="DISTANCE"
              checked={searchMode === 'DISTANCE'}
              onChange={() => { setSearchMode('DISTANCE'); setResults([]); setError(''); }}
            />
            <span>Szukaj po odległości</span>
          </label>
        </div>

        {searchMode === 'FILTER' ? (
          <div className="search-criteria">
            <div className="form-group">
              <label htmlFor="voivodeship">Województwo</label>
              <select
                id="voivodeship"
                name="voivodeship"
                value={searchCriteria.voivodeship}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">-- Wszystkie --</option>
                {dictionaries.territories.map((territory) => (
                  <option key={territory.key} value={territory.key}>
                    {territory.displayValue}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="county">Powiat</label>
              <select
                id="county"
                name="county"
                value={searchCriteria.county}
                onChange={handleChange}
                className="form-select"
                disabled={!searchCriteria.voivodeship}
              >
                <option value="">-- Wszystkie --</option>
                {getCountiesForVoivodeship().map((county) => (
                  <option key={county.key} value={county.key}>
                    {county.displayValue}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="seniorTier">Poziom</label>
              <select
                id="seniorTier"
                name="seniorTier"
                value={searchCriteria.seniorTier}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">-- Wszystkie --</option>
                {dictionaries.seniorTiers.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.displayValue}
                  </option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="search-criteria">
            <div className="form-group">
              <label htmlFor="maxDistance">Maksymalna odległość (km)</label>
              <input
                type="number"
                id="maxDistance"
                className="form-input"
                value={maxDistanceKm}
                onChange={(e) => setMaxDistanceKm(Math.max(1, Math.min(500, Number(e.target.value) || 50)))}
                min="1"
                max="500"
                step="10"
              />
            </div>

            <div className="form-group">
              <label htmlFor="seniorTierDistance">Poziom</label>
              <select
                id="seniorTierDistance"
                name="seniorTier"
                value={searchCriteria.seniorTier}
                onChange={handleChange}
                className="form-select"
              >
                <option value="">-- Wszystkie --</option>
                {dictionaries.seniorTiers.map((item) => (
                  <option key={item.key} value={item.key}>
                    {item.displayValue}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        <div className="search-actions">
          <button type="button" onClick={handleReset} className="btn btn-secondary">
            Wyczyść
          </button>
          <button type="submit" className="btn btn-primary" disabled={isLoading}>
            {isLoading ? 'Wyszukiwanie...' : 'Szukaj'}
          </button>
        </div>
      </form>

      {error && <div className="error-message">{error}</div>}

      {results.length > 0 && (
        <div className="search-results">
          <h2>Wyniki wyszukiwania ({results.length})</h2>
          <div className="results-grid">
            {results.map((team, index) => (
              <div key={index} className="result-card">
                <div className="club-details">
                  <h3>{team.sportsClubName}</h3>
                  <p><strong>Drużyna:</strong> {team.name}</p>
                  <p><strong>Województwo:</strong> {team.voivodeship}</p>
                  <p><strong>Powiat:</strong> {team.county}</p>
                  {team.tier && <p><strong>Poziom rozgrywkowy:</strong> {team.tier}</p>}
                  {searchMode === 'DISTANCE' && team.distanceKm != null && (
                    <p className="distance-info"><strong>Odległość:</strong> {team.distanceKm} km</p>
                  )}
                </div>
                <div className="card-actions">
                  <button
                    className={`btn btn-send-query ${team.isMatchRequested ? 'btn-query-sent' : 'btn-secondary'}`}
                    onClick={() => handleSendQuery(team.sportsClubId, team.sportsClubName, team.id)}
                    disabled={team.isMatchRequested || sendingQueryFor === team.id}
                  >
                    {sendingQueryFor === team.id
                      ? 'Wysyłanie...'
                      : team.isMatchRequested
                        ? 'Wysłano zapytanie ✓'
                        : 'Wyślij zapytanie'}
                  </button>
                  <button
                    className="btn btn-primary btn-show-teams"
                    onClick={() => handleShowAllTeams(team.sportsClubId, team.sportsClubName)}
                  >
                    Wyświetl wszystkie drużyny
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {showTeamsModal && (
        <div className="modal-overlay" onClick={() => setShowTeamsModal(false)}>
          <div className="modal-content teams-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Drużyny klubu: {selectedClubName}</h2>
              <button className="modal-close" onClick={() => setShowTeamsModal(false)}>×</button>
            </div>

            {isLoadingTeams ? (
              <div className="loading">Ładowanie drużyn...</div>
            ) : selectedClubTeams.length === 0 ? (
              <p className="no-teams">Brak drużyn w tym klubie</p>
            ) : (
              <div className="teams-sections-wrapper">
                <div className="team-section">
                  <h3>Drużyny seniorskie</h3>
                  <div className="teams-list">
                    {selectedClubTeams.filter(t => t.seniorTeamType !== undefined && t.seniorTeamType !== null).length === 0 ? (
                      <p className="no-teams-type">Brak drużyn seniorskich</p>
                    ) : (
                      selectedClubTeams.filter(t => t.seniorTeamType !== undefined && t.seniorTeamType !== null).map((team, idx) => (
                        <div key={idx} className="team-item">
                          <div className="team-info">
                            <div className="team-info-row">
                              <strong>Rodzaj:</strong> {formatSeniorTeamType(team.seniorTeamType)}
                            </div>
                            {team.tier && (
                              <div className="team-info-row">
                                <strong>Poziom rozgrywkowy:</strong> {formatSeniorTier(team.tier)}
                              </div>
                            )}
                            {team.gender && (
                              <div className="team-info-row">
                                <strong>Płeć:</strong> {team.gender === 'MALE' ? 'Mężczyźni' : 'Kobiety'}
                              </div>
                            )}
                            <div className="team-info-row">
                              <strong>Szuka zawodnika:</strong> {team.lookingForPlayer ? 'Tak' : 'Nie'}
                            </div>
                            <div className="team-info-row">
                              <strong>Szuka trenera:</strong> {team.lookingForCoach ? 'Tak' : 'Nie'}
                            </div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

              </div>
            )}
          </div>
        </div>
      )}

      {showMessageModal && (
        <div className="modal-overlay" onClick={handleCancelSendQuery}>
          <div className="modal-content message-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Wyślij zapytanie</h2>
              <button className="modal-close" onClick={handleCancelSendQuery}>×</button>
            </div>
            <div className="modal-body">
              <p>Wysyłasz zapytanie do klubu: <strong>{pendingMatch?.sportsClubName}</strong></p>
              <div className="form-group">
                <label htmlFor="message">Wiadomość (opcjonalnie):</label>
                <textarea
                  id="message"
                  className="form-textarea"
                  rows="5"
                  value={messageText}
                  onChange={(e) => setMessageText(e.target.value)}
                  placeholder="Wpisz swoją wiadomość..."
                  autoFocus
                  maxLength={800}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCancelSendQuery}>
                Anuluj
              </button>
              <button className="btn btn-primary" onClick={handleConfirmSendQuery}>
                Wyślij zapytanie
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SearchClubs;
